/**
 * Compiles a validated `CanonicalSkillV3Input` into an immutable
 * `CompiledSkillPlanV3`: the shared cast policy plus, per stage, the compiled
 * application/effects and the expanded attack units that stage produces.
 *
 * It deliberately does not own execution, timing arithmetic against a live
 * clock, or pricing. What it owns is the guarantee that every stage has
 * already been put through the **existing** V2 compiler before anything is
 * scheduled, so a composite skill can never reach the runtime by a path that
 * skips a check a single-stage skill would have faced. A stage the V2
 * compiler defers defers the whole plan — a cast is legal as a whole or not
 * at all.
 */
import { compileCanonicalSkill, type RuntimeSkillPlan } from './compiler.js';
import { resolveLegacyDeliveryForApplication } from './application.js';
import { deliveryPlacementMode } from '../sim/attack-pose.js';
import { planOrbitArcFeasibility } from '../sim/orbital-motion.js';
import { C1_BOOTSTRAP_TECHNICAL_PROFILE, ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ } from '../constraints/technical.js';
import { COMPOSITE_CAPABILITIES, ORBIT_CAPABILITIES, capabilityIsOpen, type OrbitMeasurementGrant } from './composite-capability.js';
import type { SkillApplicationV2 } from './types.js';
import type { CanonicalValidationIssue } from './contract.js';
import { chainFilterSource, validateCompositeSkill } from './composite-validate.js';
import { expandSpatialPattern, type ExpandedAttackUnit } from './spatial-pattern.js';
import { lowerDegenerateV3ToV2 } from './composite-upgrade.js';
import {
  isDegenerateCompositeSkill,
  type AttackStageV1,
  type CanonicalSkillV3,
  type CanonicalSkillV3Input,
  type StageTimingV1,
} from './composite-types.js';
import { CANONICAL_SKILL_V2_SCHEMA_VERSION, type CanonicalSkillV2Input } from './types.js';
import { chainCarrier, type ChainCarrier } from './chain-formation.js';

declare const compiledSkillPlanV3Brand: unique symbol;

/**
 * An orbit stage's resolved parameters, assembled from the four places that
 * already own them — and from nowhere else.
 *
 * | field | read from |
 * |---|---|
 * | `orbitRadiusM`, `initialPhasesRad` | `pattern.formation` (RING) |
 * | `angularSpeedRadPerS` | `stage.motion` |
 * | `followsCaster` | `application.placement` |
 * | `contactRadiusM` | `application.geometry` |
 * | `perTargetCooldownS` | `application.contactPolicy` |
 * | `lifetimeS` | `application.lifecycle` |
 *
 * Assembling rather than re-declaring is the point: there is no second
 * copy of any of these to drift from the authored skill.
 */
export interface CompiledOrbitPlanV1 {
  readonly orbitRadiusM: number;
  readonly angularSpeedRadPerS: number;
  /** `true` for a CASTER placement, `false` for RESOLVED_POINT. */
  readonly followsCaster: boolean;
  readonly contactRadiusM: number;
  readonly perTargetCooldownS: number;
  readonly lifetimeS: number;
  /** One per orb, in the RING's own placement order. */
  readonly initialPhasesRad: readonly number[];
}

/**
 * A chain stage's resolved parameters (`SpatialFormationV1` `CHAIN`), read from the formation, the member schedule and
 * the compiled delivery. `ricochet` is the SEQUENTIAL schedule: the next enemy is picked when a member strikes, and the
 * next member comes out `intervalTicks` later; otherwise the path is planned when the first member strikes and every
 * other member comes out that tick.
 */
export interface CompiledChainPlanV1 {
  readonly count: number;
  readonly jumpRangeM: number;
  readonly pick: 'NEAREST' | 'RANDOM';
  /** The name of the cast's skill status or mark whose carriers alone are picked. */
  readonly onlyStatus?: string;
  /** Which of the two `onlyStatus` names: a mark is `kind: 'mark'` with `tag: markId`, a status a skill-status definition. */
  readonly onlyKind?: 'STATUS' | 'MARK';
  readonly ricochet: boolean;
  readonly intervalTicks: number;
  readonly carrier: ChainCarrier;
}

/**
 * A dash stage's resolved parameters (`StageMotionV1` `DASH`): how far the caster may move, and the radius of the strike at
 * its landing (the application's own circle). `rotationOnly` is a formation whose members only turn (a fan, a radial): a
 * placed member then dashes along its own heading; every other placed member dashes to the point the formation put it at.
 */
export interface CompiledDashPlanV1 {
  readonly rangeM: number;
  readonly radiusM: number;
  readonly rotationOnly: boolean;
}

/** One stage, compiled and expanded. */
export interface CompiledStageV3 {
  readonly stageId: string;
  /** Authored position, which is also the tie-break order at a shared tick. */
  readonly stageOrder: number;
  readonly timing: StageTimingV1;
  /** The stage as the existing V2 compiler sees it. Every runtime behaviour a
   * stage has comes from here, never from a composite-only second path. */
  readonly runtimePlan: RuntimeSkillPlan;
  /** The attack units one cast of this stage produces, in stable order. */
  readonly units: readonly ExpandedAttackUnit[];
  /**
   * Present only for a travelling stage. When it is, `runtimePlan` is what
   * ONE CONTACT applies — the orb's circle resolved at wherever it touched
   * — and this says where the orb is over time. The two are complementary,
   * not alternatives.
   */
  readonly orbit?: CompiledOrbitPlanV1;
  /** Present only for a chain stage: its members after the first are released at the enemies it strikes. */
  readonly chain?: CompiledChainPlanV1;
  /** Present only for a dash stage: each member moves the caster to its goal before it strikes. */
  readonly dash?: CompiledDashPlanV1;
}

/**
 * The compiled form of one composite skill for one cast seed.
 *
 * Seed-bound because a `SCATTER` formation's placements are part of the plan:
 * the same plan replayed with the same seed must place the same meteors in
 * the same order.
 */
export interface CompiledSkillPlanV3 {
  readonly canonicalSkill: CanonicalSkillV3;
  readonly castSeed: number;
  readonly stages: readonly CompiledStageV3[];
  /** True when this is a migrated/authored plain single attack, which the
   * existing V2 path executes unchanged and which needs no composite
   * capability. */
  readonly degenerate: boolean;
  readonly [compiledSkillPlanV3Brand]: true;
}

export type CompositeCompileResult =
  | { readonly status: 'COMPILED'; readonly plan: CompiledSkillPlanV3 }
  | { readonly status: 'INVALID'; readonly issues: readonly CanonicalValidationIssue[] }
  | { readonly status: 'DEFERRED'; readonly code: string; readonly input: CanonicalSkillV3Input };

/**
 * Project one stage into the V2 skill it is, so the existing compiler can
 * judge it. Cadence, costs, source requirement and mastery come from the
 * cast and are shared by every stage — they are carried here only because
 * the V2 compiler reads them, never so a stage can pay them again. The
 * runtime charges a cast once, at the composite boundary.
 */
export type OrbitPlanResolution =
  | { readonly ok: true; readonly plan: CompiledOrbitPlanV1 }
  | { readonly ok: false; readonly path: string; readonly code: string; readonly message: string };

const DEG_TO_RAD = Math.PI / 180;

/**
 * Read the orbit's parameters out of the four owners that already hold
 * them. `validateCompositeSkill` has already refused any stage whose shape
 * disagrees with the canonical orbit form, so the checks here are the
 * narrow ones a type cannot express, and each is a refusal.
 */
export function resolveOrbitPlan(stage: Pick<AttackStageV1, 'motion' | 'pattern' | 'application'>): OrbitPlanResolution {
  const motion = stage.motion;
  if (motion === undefined || motion.kind !== 'ORBIT') {
    return { ok: false, path: 'motion', code: 'COMPOSITE_ORBIT_MOTION_MISSING', message: 'an orbit stage requires an ORBIT motion' };
  }
  const formation = stage.pattern.formation;
  if (formation.kind !== 'RING') {
    return { ok: false, path: 'pattern.formation', code: 'COMPOSITE_ORBIT_FORMATION_UNSUPPORTED', message: 'an orbit is a RING' };
  }
  const geometry = stage.application.geometry;
  if (geometry.owner !== 'SKILL_OWNED' || geometry.shape.kind !== 'CIRCLE') {
    return { ok: false, path: 'application.geometry', code: 'COMPOSITE_ORBIT_GEOMETRY_UNSUPPORTED', message: 'an orb is a skill-owned circle' };
  }
  const lifecycle = stage.application.lifecycle;
  if (lifecycle.kind !== 'PERSISTENT') {
    return { ok: false, path: 'application.lifecycle', code: 'COMPOSITE_ORBIT_LIFECYCLE_UNSUPPORTED', message: 'an orbit lives for a time' };
  }
  const placement = stage.application.placement;
  if (placement.kind !== 'CASTER' && placement.kind !== 'RESOLVED_POINT') {
    return { ok: false, path: 'application.placement', code: 'COMPOSITE_ORBIT_PLACEMENT_UNSUPPORTED', message: 'the orbit centre is the placement' };
  }

  // The RING's own placement formula, not a second one: member i sits at
  // initialAngleDeg + i x 360/count, which is exactly where the orb starts.
  const step = (2 * Math.PI) / formation.count;
  const initialPhasesRad = Array.from({ length: formation.count }, (_unused, i) => formation.initialAngleDeg * DEG_TO_RAD + step * i);

  return {
    ok: true,
    plan: {
      orbitRadiusM: formation.radiusM,
      angularSpeedRadPerS: motion.angularSpeedRadPerS,
      followsCaster: placement.kind === 'CASTER',
      contactRadiusM: geometry.shape.radiusM,
      perTargetCooldownS: stage.application.contactPolicy.perTargetCooldownS ?? 0,
      lifetimeS: lifecycle.durationS,
      initialPhasesRad: Object.freeze(initialPhasesRad),
    },
  };
}

/** The application of ONE CONTACT of an orbit: at the orb, instantaneous, once per target (the axes the comment at its use explains). */
export function orbitContactApplication(application: SkillApplicationV2): SkillApplicationV2 {
  return {
    ...application,
    placement: { kind: 'RESOLVED_POINT' },
    lifecycle: { kind: 'INSTANT' },
    contactPolicy: { hitPolicy: 'ONCE_PER_TARGET', termination: 'FIRST_VALID_CONTACT' },
  };
}

/** A Mastery policy realizes against a source-relative damage leaf (`safety.ts`); a stage of crowd control alone has none to realize. */
export function stageRealizesMastery(effects: AttackStageV1['effects']): boolean {
  return effects.some((effect) => (effect.kind === 'damage' || effect.kind === 'dot') && effect.sourceRelative !== undefined);
}

function stageAsV2(skill: CanonicalSkillV3Input, stage: AttackStageV1): CanonicalSkillV2Input {
  const { schemaVersion: _v3, stages: _stages, statuses: _statuses, masteryPolicy, ...shared } = skill;
  return {
    ...shared,
    ...(masteryPolicy !== undefined && stageRealizesMastery(stage.effects) ? { masteryPolicy } : {}),
    // A distinct id per stage keeps telemetry and runtime attack attribution
    // able to name which stage acted, without two stages colliding in any
    // id-keyed map. The cast's own identity stays `skill.id`.
    id: `${skill.id}#${stage.stageId}`,
    schemaVersion: CANONICAL_SKILL_V2_SCHEMA_VERSION,
    application: stage.application,
    effects: stage.effects,
  } as CanonicalSkillV2Input;
}

/**
 * Compile a composite skill.
 *
 * Order matters and is fail-closed throughout: structure first, then every
 * stage through the existing V2 compiler, then expansion. A failure at any
 * step returns without a plan, so no caller can observe a partially compiled
 * cast.
 */
export function compileCompositeSkill(input: unknown, castSeed = 0, grant?: OrbitMeasurementGrant): CompositeCompileResult {
  const validated = validateCompositeSkill(input);
  if (!validated.ok) return { status: 'INVALID', issues: validated.issues };
  const skill = validated.skill;

  if (!Number.isFinite(castSeed)) {
    return { status: 'INVALID', issues: [{ path: 'castSeed', code: 'COMPOSITE_CAST_SEED_INVALID', message: 'castSeed must be finite' }] };
  }

  const stages: CompiledStageV3[] = [];
  for (const [stageOrder, stage] of skill.stages.entries()) {
    // ── Travelling stages take their own route ──────────────────────────
    //
    // An orbit must NOT be handed to `compileCanonicalSkill`, and not as a
    // convenience: three independent gates there would each mishandle it.
    // `COOLDOWN_PER_TARGET` is deferred for every V2 shape
    // (`compiler.ts:61-62`); a PERSISTENT skill-owned circle is lowered to
    // a ground area, which an orbit is not; and a caster-placed PERSISTENT
    // circle is refused as `CASTER_CIRCLE_LIFECYCLE_RUNTIME_UNIMPLEMENTED`
    // because the legacy `Delivery` cannot carry a circle that follows its
    // caster over time — which is precisely a FOLLOW_CASTER orbit. (Only an
    // INSTANT caster circle lowers, to a one-moment area at the caster.)
    // Routing around all three is what
    // lets those V2 deferrals stay exactly as they are: opening an orbit
    // does not open `COOLDOWN_PER_TARGET` for anything else.
    //
    // The refusal below is the capability registry's, not this module's:
    // with all five orbit capabilities open (Gate P8) this is the seam
    // where the orbit execution plan is built, and should any close again,
    // a plan that cannot be executed is not compiled into one that looks
    // as if it could.
    if (stage.motion?.kind === 'ORBIT') {
      const closed = ORBIT_CAPABILITIES.find((id) => !capabilityIsOpen(id, grant));
      if (closed !== undefined) {
        return { status: 'DEFERRED', code: COMPOSITE_CAPABILITIES[closed].deferralCode, input: skill };
      }

      const orbit = resolveOrbitPlan(stage);
      if (!orbit.ok) {
        return { status: 'INVALID', issues: [{ path: `stages[${stageOrder}].${orbit.path}`, code: orbit.code, message: orbit.message }] };
      }

      // Schema-valid, but outside the ratified technical envelope: an arc
      // this fast needs more sub-steps per tick than the profile allows, so
      // every tick of it would be refused — and a refused tick commits
      // nothing, lifetime included, so the orb would never expire. Judged
      // at the profile's own reference tick; a world at another rate
      // re-checks at its own in `beginCast`. DEFERRED, not INVALID: the
      // skill is well-formed, the envelope is what cannot carry it.
      const arc = planOrbitArcFeasibility(
        orbit.plan.orbitRadiusM,
        orbit.plan.angularSpeedRadPerS,
        1 / ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ,
        C1_BOOTSTRAP_TECHNICAL_PROFILE.orbit.contactToleranceM,
        C1_BOOTSTRAP_TECHNICAL_PROFILE.orbit.maxContactStepsPerInstancePerTick,
      );
      if (!arc.ok) return { status: 'DEFERRED', code: 'COMPOSITE_ORBIT_PRECISION_ENVELOPE_EXCEEDED', input: skill };

      // What ONE CONTACT applies. The orbit owns where and when; this owns
      // what happens there, and it goes through the same V2 compiler every
      // other stage uses so damage, mastery and source semantics come from
      // one place. Three axes are rewritten for the contact, and each is a
      // statement about the contact rather than about the orbit:
      // the contact happens AT the orb (RESOLVED_POINT, not the orbit's
      // centre anchor), it is instantaneous (the orbit carries the
      // lifetime), and it lands once (the orbit's own hit map carries the
      // rehit interval, per orb, which is what COOLDOWN_PER_TARGET could
      // never express for a shared V2 delivery).
      const contactApplication = orbitContactApplication(stage.application);
      const compiledContact = compileCanonicalSkill(stageAsV2(skill, { ...stage, application: contactApplication }), { compositeStage: true });
      if (compiledContact.status === 'INVALID') {
        return {
          status: 'INVALID',
          issues: compiledContact.issues.map((issue) => ({ ...issue, path: `stages[${stageOrder}].${issue.path}` })),
        };
      }
      if (compiledContact.status === 'DEFERRED') {
        return { status: 'DEFERRED', code: compiledContact.code, input: skill };
      }

      const expandedOrbit = expandSpatialPattern(stage.pattern, castSeed);
      if (!expandedOrbit.ok) {
        return {
          status: 'INVALID',
          issues: [{ path: `stages[${stageOrder}].pattern`, code: expandedOrbit.code, message: expandedOrbit.message }],
        };
      }

      stages.push({
        stageId: stage.stageId,
        stageOrder,
        timing: stage.timing,
        runtimePlan: compiledContact.plan,
        units: expandedOrbit.units,
        orbit: orbit.plan,
      });
      continue;
    }

    const compiled = compileCanonicalSkill(stageAsV2(skill, stage), { compositeStage: true });
    if (compiled.status === 'INVALID') {
      // Re-path the issue onto the stage that owns it, so an author is told
      // which stage is wrong rather than being handed a root-level complaint.
      return {
        status: 'INVALID',
        issues: compiled.issues.map((issue) => ({ ...issue, path: `stages[${stageOrder}].${issue.path}` })),
      };
    }
    if (compiled.status === 'DEFERRED') {
      // One deferred stage defers the cast. Compiling the rest and running
      // them would silently deliver a smaller skill than the one authored.
      return { status: 'DEFERRED', code: compiled.code, input: skill };
    }

    const expanded = expandSpatialPattern(stage.pattern, castSeed);
    if (!expanded.ok) {
      return {
        status: 'INVALID',
        issues: [{ path: `stages[${stageOrder}].pattern`, code: expanded.code, message: expanded.message }],
      };
    }

    const formation = stage.pattern.formation;
    // The validator has held a dash's application to a skill-owned circle at the caster.
    const shape = stage.application.geometry.owner === 'SKILL_OWNED' ? stage.application.geometry.shape : undefined;
    const dash: CompiledDashPlanV1 | undefined = stage.motion?.kind === 'DASH' && shape?.kind === 'CIRCLE'
      ? { rangeM: stage.motion.rangeM, radiusM: shape.radiusM, rotationOnly: (ROTATION_ONLY_FORMATION_KINDS as readonly string[]).includes(formation.kind) }
      : undefined;
    let chain: CompiledChainPlanV1 | undefined;
    if (formation.kind === 'CHAIN') {
      const runtimeSkill = compiled.plan.runtimeSkill;
      const carried = chainCarrier(runtimeSkill.delivery, runtimeSkill.weaponContactApplication !== undefined, formation.jumpRangeM, formation.only !== undefined, dash !== undefined);
      if (!carried.ok) {
        return { status: 'INVALID', issues: [{ path: `stages[${stageOrder}].application`, code: carried.code, message: carried.message }] };
      }
      const schedule = stage.pattern.memberSchedule;
      chain = {
        count: formation.count, jumpRangeM: formation.jumpRangeM, pick: formation.pick,
        ...(formation.only === undefined ? {} : {
          onlyStatus: formation.only.skillStatus,
          onlyKind: chainFilterSource(skill.stages as unknown as readonly Record<string, unknown>[], skill.statuses, stageOrder, formation.only.skillStatus)?.kind ?? 'STATUS',
        }),
        ricochet: schedule.kind === 'SEQUENTIAL', intervalTicks: schedule.kind === 'SEQUENTIAL' ? schedule.intervalTicks : 0,
        carrier: carried.carrier,
      };
    }

    // A chain's members are placed at the enemies it strikes, never at the cast's frame, so they never coincide there; a
    // dash's members each move the caster along their own heading, so they never coincide either.
    const coincident = chain === undefined && dash === undefined ? rotationInvariantCoincidentMembers(compiled.plan.canonicalSkill.application, expanded.units) : undefined;
    if (coincident !== undefined) {
      return {
        status: 'INVALID',
        issues: [{
          path: `stages[${stageOrder}].pattern.formation`,
          code: 'COMPOSITE_FORMATION_ROTATION_INVARIANT_MEMBERS',
          message: `a ${stage.pattern.formation.kind} formation only turns its ${coincident} members, and this stage's `
            + 'ground-anchored delivery is the same footprint whichever way it faces, so every member lands on one spot — '
            + 'coincident members are a hidden multiplier (SINGLE refuses memberCount > 1 for the same reason); '
            + 'use volleys to repeat, or a formation that places members apart',
        }],
      };
    }

    stages.push({
      stageId: stage.stageId,
      stageOrder,
      timing: stage.timing,
      runtimePlan: compiled.plan,
      units: expanded.units,
      ...(chain === undefined ? {} : { chain }),
      ...(dash === undefined ? {} : { dash }),
    });
  }

  const plan = {
    canonicalSkill: skill,
    castSeed,
    stages,
    degenerate: isDegenerateCompositeSkill(skill),
  } as unknown as CompiledSkillPlanV3;

  return { status: 'COMPILED', plan };
}

/**
 * Formation kinds whose members differ ONLY by heading — their expanded
 * transforms turn and never translate (`spatial-pattern.ts`). Pinned against
 * the real expansion by `test/composite-rotation-invariant-formation.test.ts`.
 */
export const ROTATION_ONLY_FORMATION_KINDS = Object.freeze(['FAN', 'RADIAL'] as const);

/** True when this application's delivery is anchored at a ground point and
 * ignores heading — the half of the rule a formation kind cannot see. */
export function applicationIsRotationInvariant(application: SkillApplicationV2): boolean {
  const lowered = resolveLegacyDeliveryForApplication(application);
  return lowered.ok && deliveryPlacementMode(lowered.delivery.type) === 'POINT';
}

/**
 * A formation whose members differ only by heading, on a delivery that has
 * no heading. A ground area is placed at the member pose's ORIGIN (the
 * placement owner's `POINT` mode, `sim/attack-pose.ts`) and its disc is the
 * same whichever way it faces, so members that share an origin within one
 * volley are one footprint several times over. FAN and RADIAL are exactly
 * this for a circle: their transforms turn and never translate.
 *
 * Overlap between members placed APART (SCATTER, PARALLEL, RING — P7's
 * "overlap A") is untouched: those members are at different points and only
 * happen to reach the same target. Only structural coincidence — guaranteed
 * on every cast by the formation itself — is refused.
 *
 * Returns the member count of the first offending volley, else `undefined`.
 */
function rotationInvariantCoincidentMembers(application: SkillApplicationV2, units: readonly ExpandedAttackUnit[]): number | undefined {
  if (!applicationIsRotationInvariant(application)) return undefined;
  const originsByVolley = new Map<number, string[]>();
  for (const unit of units) {
    const origins = originsByVolley.get(unit.volleyIndex) ?? [];
    for (const transform of unit.transforms) origins.push(`${transform.x},${transform.y}`);
    originsByVolley.set(unit.volleyIndex, origins);
  }
  for (const origins of originsByVolley.values()) {
    if (origins.length > 1 && new Set(origins).size < origins.length) return origins.length;
  }
  return undefined;
}

/**
 * The V2 skill a degenerate plan is, for callers that can take the existing
 * single-stage path. `undefined` for anything genuinely composite — never a
 * reduced stand-in.
 */
export function degenerateRuntimePlan(plan: CompiledSkillPlanV3): RuntimeSkillPlan | undefined {
  if (!plan.degenerate) return undefined;
  return plan.stages[0]?.runtimePlan;
}

/** Total independent attack units one cast of this plan resolves. */
export function planAttackUnitCount(plan: CompiledSkillPlanV3): number {
  return plan.stages.reduce((sum, stage) => sum + stage.units.length, 0);
}

export { lowerDegenerateV3ToV2 };
